import React, { useContext, useEffect, useState } from 'react';
import { Breadcrumb, Button, OverlayTrigger, Table } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiChevronLeft, BiSolidReport } from "react-icons/bi";
import { BsFileEarmarkPdfFill, BsFileEarmarkWordFill, BsChevronLeft } from "react-icons/bs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from 'react-toastify';
import { EventContext, LoaderContext, RegulationContext } from '../../../../App';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import { EventType, LS_AUTHORITY_KEY, LS_SELECTED_MAT_ISSUE_YEAR, currentYear, startYear } from '../../../../core/constants/common';
import MaterialIssueService from '../../../../core/service/MaterialIssueService';
import ReportService from '../../../../core/service/ReportService';
import Utils from '../../../../core/utilities/Utils';
import assessment_pic from "../../../../images/Assessment.png";
import ReportExportModal from '../../../SubscriberSupplierAdmin/Reporting/ReportExportModal';
import './MaterialityAssessment.scss';


const MatReportList = ({ t }) => {
	// Hooks must be called unconditionally at the top level
	const { glbRegulation } = useContext(RegulationContext);
	const { setLoaderDisplay } = useContext(LoaderContext);
	const { setEvent } = useContext(EventContext);
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	
	const [scores, setScores] = useState([]);
	const [reportToExport, setReportToExport] = useState({ id: 0 });
	const [selectedScore, setSelectedScore] = useState(null);
	const [showGenerateAndProceedConfirmation, setShowGenerateAndProceedConfirmation] = useState({ generateReport: false, proceedToMatReport: false });
	const [generatedReportId, setGeneratedReportId] = useState(0);
	
	// Get localStorage values needed for useState initializations
	const lsDat = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const companyId = lsDat?.profile?.company?.id;
	const companyName = lsDat?.profile?.company?.legalName;
	const selectedYear = localStorage.getItem(LS_SELECTED_MAT_ISSUE_YEAR) ?? null;
	const year = new Date().getFullYear();
	const [filter, setFilter] = useState({
		year: selectedYear ?? `${year}`,
	});
	
	// Non-hook code after all hooks (including loops)
	const yearOptions = [];
	for (let i = currentYear; i >= startYear; i--) {
		yearOptions.push(<option value={i} key={i}>{i}</option>);
	}
	
	// Non-hook code after all hooks
	const glbRegulationId = glbRegulation?.id ?? "";
	const period = searchParams.get('period') ?? '';
	const assYear = period ? period.substring(0, 4) : '';
	const assessmentId = searchParams.get('aId') ?? 0;

	useEffect(() => {
		localStorage.setItem(LS_SELECTED_MAT_ISSUE_YEAR, filter.year);
		getReportVersions();
		getReportList();
	}, [filter]);

	const getReportVersions = () => {
		setLoaderDisplay(true);
		MaterialIssueService.getSCVersions(companyId, filter.year).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;
			res.data.sort((a, b) => b.versionId - a.versionId);
			res.data = res.data.filter((item) => {
				return item?.status != 'DRAFT';
			})
			setScores(res.data ?? []);
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};

	const getReportList = () => {
		setLoaderDisplay(true);
		MaterialIssueService.getReportLists(10, 0, companyId, filter.year).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};

	const generatePDF = (reportId, reportName, openPreview = false) => {
		setLoaderDisplay(true);
		ReportService.generateESRSPDF(reportId)
			.then((res) => {
				setLoaderDisplay(false);
				let blob = new Blob([res.data], { type: 'application/pdf' });
				// Creating new object of PDF file
				const fileURL = window.URL.createObjectURL(blob);
				let link = document.createElement('a');
				link.href = fileURL;
				if (openPreview) {
					link.target = '_blank';
				} else {
					link.download = `${reportName}.pdf`;
				}
				link.click();
			})
			.catch((error) => {
				setLoaderDisplay(false);
				toast.error(error.title, {
					position: toast.POSITION.TOP_RIGHT,
				});
			});
	};

	const generateReport = () => {
		setLoaderDisplay(true);
		MaterialIssueService.genReport(companyId, selectedScore.versionId, companyName).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;
			// toast.success("Report generated successfully", {
			//     position: toast.POSITION.TOP_RIGHT,
			// });
			setGeneratedReportId(res.data);
			getReportVersions();
			setShowGenerateAndProceedConfirmation({
				generateReport: false,
				proceedToMatReport: true,
			});
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};

	const gotoReport = (reportId) => {
		navigate('/company_admin/dmat/report?reportId=' + reportId);
	}

	const generateReportConfirmation = (val) => {
		setShowGenerateAndProceedConfirmation({ generateReport: false, proceedToMatReport: false });
		if (val) {
			generateReport();
		}
	}

	const navigateToMatReport = (val) => {
		setShowGenerateAndProceedConfirmation({ generateReport: false, proceedToMatReport: false });
		if (val) {
			navigate(`/company_admin/dmat/report?reportId=` + generatedReportId);
		}
	}

	const renderHeader = () => {
		return (
			<>
				<div className="List_header pb-0">
					<Breadcrumb className='h5'>
						<Breadcrumb.Item onClick={() => navigate(`/company_admin/dmat/stats`)}>{t("Materiality.label")}</Breadcrumb.Item>
						<Breadcrumb.Item active>{t("Reports.label")}</Breadcrumb.Item>
					</Breadcrumb>
					<div className="header-actions">
						<select className="form-select  w-auto form-select-sm" value={filter.year}
							onChange={(e) => {
								setFilter({ ...filter, year: e.target.value });
								setEvent({
									event: EventType.DOUBLE_MAT_YEAR_CHANGED,
									data: { year: e.target.value },
								});
							}}>
							{yearOptions}
						</select>
						<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("NavigateBackAndNext.Scorecard"))}>
							<Button variant='secondary' className='back-btn btn-sm scorecard-btn' onClick={() => navigate(`/company_admin/dmat/scorecard/default`)}>
								<BsChevronLeft /> <span>{t("Back.label")}</span>
							</Button>
						</OverlayTrigger>
					</div>
				</div>
			</>
		);
	};

	const blankCardToRender = () => {
		return (
			<div className="Assessment_blankcard">
				<div>
					<div className="assessment_pic">
						<img src={assessment_pic} />
					</div>
					<h4 className="mb-4">{t('ReportBlankHeader.label')}</h4>
				</div>
			</div>
		);
	};

	const groupByAssessment = (data) => {
		const grouped = {};

		data.forEach((item) => {
			const parts = item.assessmentName.split('-').map(part => part.trim());
			const yearVersion = parts.find(part => part.match(/^\d{4}\sV\d+$/))?.replace(' V', ' - Version ');

			if (yearVersion) {
				if (!grouped[yearVersion]) {
					grouped[yearVersion] = [];
				}
				grouped[yearVersion].push(item);
			}
		});

		return grouped;
	};

	const renderTableHeader = () => {
		return (
			<thead>
				<tr>
					<th>{t('SurveyYear.label')} / {t("Version.label")}</th>
					<th>{t('ScoreCardVersion.label')}</th>
					<th>{t('Type.label')}</th>
					<th className='text-center'>{t('Status.label')}</th>
					<th className='text-end'></th>
				</tr>
			</thead>
		);
	};

	const renderRows = () => {
		return <>
			{
				Object.entries(groupByAssessment(scores)).map(([group, items], groupIndex) => (
					<React.Fragment key={`group-${groupIndex}`}>
						<tr className='ScoreCardYear'>
							<td colSpan="6"><strong>{group}</strong></td>
						</tr>
						{items.map((score, index) => (
							<tr key={`item-${groupIndex}-${index}`}>
								<td></td>
								<td>{`Version ${score.version.slice(1)}`}</td>
								<td>{score.materialityVersionType === 'SINGLE' ? 'Impact Materiality' : 'Double Materiality'}</td>
								<td className="text-center">
									{
										score.reportId == 0 &&
										<span className="badge bg-badge-Inprogress rounded-pill py-1">{'Not generated'}</span>
									}
									{
										score.reportId > 0 &&
										<span className="badge bg-badge-Finalized rounded-pill py-1">{'Generated'}</span>
									}
								</td>
								<td className='text-end'>
									{
										score.reportId > 0 &&
										<>
											<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("ViewReport.label"))}>
												<button className='btn btn-blank_outline  ' onClick={() => gotoReport(score.reportId)}>
													<BiSolidReport className='report' />
												</button>
											</OverlayTrigger>
											<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("DownloadAsPDF.label"))}>
												<button className='btn btn-blank_outline  ' onClick={() => generatePDF(score.reportId, score.assessmentName)}>
													<BsFileEarmarkPdfFill className='pdf' />

												</button>
											</OverlayTrigger>
											<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("DownloadAsWord.label"))}>
												<button className='btn btn-blank_outline  ' onClick={() => setReportToExport({ id: score.reportId, exportAs: 'WORD' })}>
													<BsFileEarmarkWordFill className='word' />
												</button>
											</OverlayTrigger>
										</>
									}
									{
										score.reportId == 0 && score.status == 'FINALIZED' &&
										<>
											{/* <OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("GenerateReport.label"))}> */}
											<button className='btn btn-secondary  ' onClick={() => {
												setSelectedScore(score);
												setShowGenerateAndProceedConfirmation({
													...showGenerateAndProceedConfirmation,
													generateReport: true,
												});
											}}> {t("GenerateReport.label")}  </button>
											{/* </OverlayTrigger> */}
										</>
									}
								</td>
							</tr>
						))}
					</React.Fragment>
				))
			}
		</>;
	};

	return (
		<div className='ScoreCardbox 2'>
			{renderHeader()}
			{
				scores.length > 0 &&
				<div className='ScoreCardTable_003 mt-2'>
					<Table className='mb-0'>
						{renderTableHeader()}
						<tbody className='table-border-bottom-0'>
							{renderRows()}
						</tbody>
					</Table>
				</div>
			}
			{scores.length === 0 && blankCardToRender()}
			{
				reportToExport?.id > 0 &&
				<ReportExportModal
					reportId={reportToExport?.id}
					exportAs={reportToExport?.exportAs}
					onHide={() => setReportToExport({ id: 0 })}
					activeView={'DOUBLE_MAT'}
				/>
			}
			{
				showGenerateAndProceedConfirmation.generateReport &&
				<ConfirmationModal
					type={'GenerateReport'}
					modalShow={true}
					// title={selectedScore.assessmentName}
					messageText={t("GenerateReportConfirmation.label")}
					callbackModal={generateReportConfirmation}
					confirmBtn={t("Proceed.label")}
					CancelBtn={t("Cancel.label")}
				/>
			}
			{
				showGenerateAndProceedConfirmation.proceedToMatReport &&
				<ConfirmationModal
					type={'GenerateReport'}
					modalShow={true}
					title={t('ProceedToMatReportTitle.label')}
					messageText={t("ViewMatReport.label")}
					callbackModal={navigateToMatReport}
					confirmBtn={t("Proceed.label")}
					CancelBtn={t("Cancel.label")}
				/>
			}
		</div>
	)
};

export default withTranslation()(MatReportList);